import { ReactNode, useCallback } from "react"

import { IconFile } from "@tabler/icons-react"
import { DropzoneOptions, useDropzone } from "react-dropzone"

import { cn } from "@/lib/utils"

import { Button } from "./Button"
import { IconWrapper } from "./IconWrapper"

export interface FileDropZoneProps {
  children?: ReactNode
  fileHandler: (files: File[]) => void
  className?: string
  accept?: DropzoneOptions["accept"]
  maxFiles?: number
  alwaysShow?: boolean
  dropZoneText?: string
}

export const FileDropZone = ({
  children,
  className,
  fileHandler,
  accept,
  maxFiles,
  alwaysShow = false,
  dropZoneText = "Drag and drop files here",
}: FileDropZoneProps) => {
  const onDrop = useCallback<NonNullable<DropzoneOptions["onDrop"]>>(
    (files) => {
      fileHandler(files)
    },
    [fileHandler],
  )

  const {
    getRootProps,
    isDragActive,
    open: openFileInput,
    getInputProps,
  } = useDropzone({
    onDrop,
    noClick: true,
    accept,
    maxFiles,
  })

  return (
    <div
      {...getRootProps({
        className: "relative h-full",
      })}
    >
      <input {...getInputProps()} />
      <div
        className={cn(
          {
            "flex h-full w-full items-center justify-center rounded-md border-2 border-dashed border-brand-secondary-400 bg-accent-1-200 p-4 transition focus:outline-hidden":
              isDragActive || alwaysShow,
            "bg-accent-1-300": alwaysShow && isDragActive,
          },
          className,
        )}
      >
        {isDragActive || alwaysShow ? (
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <IconWrapper className="bg-accent-1-300">
              <IconFile size={20} />
            </IconWrapper>
            <div className="flex flex-col items-center gap-1 text-body1 font-normal text-grey-500">
              <span>{dropZoneText}</span>
              <span>or</span>
              <Button
                variant="secondary"
                onClick={openFileInput}
                noBorder
                className="w-fit"
              >
                Browse...
              </Button>
            </div>
          </div>
        ) : (
          <>{children}</>
        )}
      </div>
    </div>
  )
}
